import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StepService } from 'services/professional/step/step.service';
import { ROLE_CODES, ROLE_NAMES } from 'constants/role.constants';
import { isStepSubmitted } from 'utils/step.helpers';
import { COMPLIANCE_CRITERIA } from 'constants/log-step.constants';
import { LogService } from 'services/log/log.service';
import { MessageQueryService } from '@certemy/ui-components';
import { LOG_STEP_TABS } from 'components/modal-windows/step-details/log-details/modal-content/submit-tabs/submit-tabs.component';
import { ComputedFrom } from '@certemy/common';
import { getFormattedEntries } from './modal-content/corrections-form/correction-form.component.helpers';
import { VERIFICATION_TYPE } from 'constants/constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export enum PAGES {
  INVITATION,
  INV_CONFIRM,
  SUBMIT_CONTENT,
  CORRECTIONS_CONTENT,
  ENTRIES,
  CRUD_ENTRY,
  RESET_CONFIRM,
}

@UntilDestroy()
@Component({
  selector: 'cm-log-details',
  templateUrl: './log-details.component.html',
  styleUrls: ['./log-details.component.scss', '../step-details.shared.scss'],
})
export class LogDetailsComponent implements OnInit, OnDestroy {
  @Input() set logStep(step) {
    this.step = step;
    this.updateStepFlow();
  }
  @Input() workflowTypeId: number;

  @Output() updateStep = new EventEmitter();
  @Output() toggleMode = new EventEmitter();
  PAGES = PAGES;
  page: number;
  selectedEntry;
  private step;
  @ComputedFrom('step')
  get isEntriesCriteria(): boolean {
    return this.step.content.compliance_criteria_type_id === COMPLIANCE_CRITERIA.ENTRIES;
  }

  @ComputedFrom('step')
  get stepEditableStatus() {
    return !isStepSubmitted(this.step.status_id);
  }

  @ComputedFrom('step')
  get initialPage() {
    return this.step.content.role === ROLE_NAMES[ROLE_CODES.PROFESSIONAL] ? PAGES.ENTRIES : PAGES.INVITATION;
  }
  readonly COMPACT_PAGES = [PAGES.SUBMIT_CONTENT, PAGES.CORRECTIONS_CONTENT, PAGES.CRUD_ENTRY, PAGES.RESET_CONFIRM];
  entries = [];

  constructor(
    private activeModal: NgbActiveModal,
    private stepService: StepService,
    private logService: LogService,
    private messageQueryService: MessageQueryService,
  ) {}

  ngOnInit() {
    this.page = this.initialPage;
  }

  ngOnDestroy() {}

  onPageChange(page) {
    this.toggleMode.emit();
    this.page = page;
  }

  closeModal() {
    this.activeModal.close();
  }

  dismissModal() {
    this.activeModal.dismiss();
  }

  onOpenEntryForm(data) {
    this.selectedEntry = data.entry;
    this.toggleMode.emit();
    this.page = data.page;
  }

  goToPage(page: number): void {
    if (page === this.PAGES.ENTRIES) {
      this.updateStep.emit();
    }

    this.changePage(page);
  }

  onResetConfirm() {
    this.stepService.resetStep(this.step.step_instance_id).subscribe(() => {
      this.updateStep.emit();
      this.messageQueryService.addMessage('Step has been successfully reset');
      this.changePage(this.initialPage);
    });
  }

  submit(formData: { type: LOG_STEP_TABS; entryData; saveAsDraft?: boolean }) {
    formData.type === LOG_STEP_TABS.SINGLE
      ? this.createSingleEntry(formData.entryData, formData.saveAsDraft)
      : this.createMultipleEntries(formData.entryData);
  }

  private createSingleEntry(entryData, saveAsDraft) {
    const preparedPayload = this.logService.prepareEntryPayload({
      type: 'single',
      data: entryData,
      fields: this.step.log_form.fields,
      saveAsDraft,
    });
    this.logService
      .createEntry(this.step.step_instance_id, preparedPayload)
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.showMessageAfterFormSubmit({ isLastEntity: !data.result.remaining });
        this.goToPage(PAGES.ENTRIES);
      });
  }

  private createMultipleEntries(entryData) {
    this.logService
      .parseFileWithLogFormData(this.step.step_instance_id, entryData)
      .pipe(untilDestroyed(this))
      .subscribe(({ log_forms, valid_data, remaining }) => {
        if (valid_data) {
          this.showMessageAfterFormSubmit({ isLastEntity: !remaining, isMultipleUpload: true });
          this.goToPage(PAGES.ENTRIES);
        } else {
          this.entries = getFormattedEntries(log_forms);
          this.goToPage(PAGES.CORRECTIONS_CONTENT);
        }
      });
  }

  private changePage(page: number) {
    if (this.isToggleRequired(this.page, page)) {
      this.toggleMode.emit();
    }

    this.page = page;
  }

  private isToggleRequired(currentPage: number, nextPage: number): boolean {
    return this.COMPACT_PAGES.includes(currentPage) !== this.COMPACT_PAGES.includes(nextPage);
  }

  private updateStepFlow() {
    this.step._hoursFlow = this.step.content.compliance_criteria_type_id === COMPLIANCE_CRITERIA.HOURS;
    this.step._entriesFlow = this.step.content.compliance_criteria_type_id === COMPLIANCE_CRITERIA.ENTRIES;
  }

  private showMessageAfterFormSubmit(showMessageConditions: { isLastEntity: boolean; isMultipleUpload?: boolean }) {
    if (showMessageConditions.isLastEntity) {
      this.messageQueryService.addMessage(
        `All required ${this.isEntriesCriteria ? 'entries' : 'hours'} are submitted, please use
        ${
          [VERIFICATION_TYPE.VERIFICATION_REQUIRED, VERIFICATION_TYPE.DOUBLE_VERIFICATION_REQUIRED].includes(
            this.step.verificationType,
          )
            ? ''
            : ' complete'
        } option
        ${
          [VERIFICATION_TYPE.VERIFICATION_REQUIRED, VERIFICATION_TYPE.DOUBLE_VERIFICATION_REQUIRED].includes(
            this.step.verificationType,
          )
            ? ' to send them for verification'
            : ''
        } to complete step.`,
        6000,
      );
    } else {
      this.messageQueryService.addMessage(
        `Your ${showMessageConditions.isMultipleUpload ? 'entries have' : 'entry has'} been successfully submitted`,
      );
    }
  }
}
